import { IPost } from '@sdk/contracts';
import { Empty, Button } from 'antd';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';

import { CreatePost } from './CreatePost';
import { Wrapper, FooterWrapper } from './PostWidget.styles';

import { Icon } from '@/components/Icon';
import { LoadingCircleSpinner } from '@/components/Loaders';
import { PostForm } from '@/components/PostForm';
import { PostItem } from '@/components/PostItem';
import { PostViewer } from '@/components/PostViewer';
import { WidgetBase } from '@/components/WidgetBase';
import { usePostsList, useProfile } from '@/store';
import { WidgetProps } from '@/types';

type Props = {
  editable?: boolean;
} & WidgetProps;

const LOADER_SIZE = 40;
const ICON_SIZE = 16;

export const PostWidget: React.FC<Props> = ({ editable, ...props }) => {
  const { posts = [], isPending, loadMorePosts, resetFilters } = usePostsList();
  const { t } = useTranslation('CommonDashboardTranslations');
  const { roleCheck } = useProfile();
  const navigate = useNavigate();

  const { isAdmin, isManager, isTeacher } = roleCheck;
  const isEditable = isAdmin || isManager || isTeacher;

  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
    rootMargin: '10px'
  });

  useEffect(() => {
    if (inView && !isPending) {
      loadMorePosts();
    }
  }, [inView, isPending, loadMorePosts]);

  useEffect(() => {
    return () => {
      resetFilters();
    };
  }, [resetFilters]);

  const shouldBeSetAsFeatured = useCallback((post: IPost, index: number) => {
    const isFirstOrSecond = index === 0 || index === 1;
    return isFirstOrSecond && post.isFeatured;
  }, []);

  return (
    <>
      <WidgetBase
        padding="0"
        title={t('NEWS')}
        addon={<CreatePost editable={editable} />}
        {...props}>
        <>
          <Wrapper>
            {posts.length === 0 && !isPending && <Empty description={t('NO_NEWS_TO_SHOW')} />}
            {posts.map((post, index) => (
              <PostItem
                key={post._id}
                post={post}
                isFeatured={shouldBeSetAsFeatured(post, index)}
                isEditable={false}
                canOpenEditor={true}
              />
            ))}
            {isPending && <LoadingCircleSpinner size={LOADER_SIZE} />}
            <div ref={loadMoreRef} />
          </Wrapper>
          <FooterWrapper>
            <Button
              type="text"
              icon={<Icon i="ArrowRight" size={ICON_SIZE} />}
              iconPosition="end"
              onClick={() => navigate('/communication/post-panel')}>
              {t('GO_TO_NEWS_PANEL')}
            </Button>
          </FooterWrapper>
        </>
      </WidgetBase>
      <PostForm isEditable={isEditable} />
      <PostViewer />
    </>
  );
};
